import React, { useState, useEffect } from 'react';
import { 
  BarChart3,
  TrendingUp,
  Eye,
  Calendar,
  Activity,
  Zap
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar } from 'recharts';
import toast from 'react-hot-toast';
import ApiService from '../../services/ApiService';

const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#F97316', '#06B6D4', '#84CC16'];

function AITrafficAnalytics({ isMenuOpen }) {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('30_days');

  useEffect(() => {
    loadAnalytics();
  }, [timeRange]);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      const data = await ApiService.getAnalyticsOverview();
      setAnalytics(data);
    } catch (error) {
      console.error('Failed to load analytics:', error);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const simulateTimeSeriesData = () => {
    // Simulate daily traffic data for the chart
    const days = 30;
    const data = [];
    for (let i = days; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      data.push({
        date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        traffic: Math.floor(Math.random() * 100) + 20,
        queries: Math.floor(Math.random() * 50) + 10,
      });
    }
    return data;
  };

  const timeSeriesData = simulateTimeSeriesData();

  const aiEngines = [
    'ChatGPT', 'Claude', 'Perplexity', 'Google Bard', 'Bing Chat', 
    'Jasper', 'Copy.ai', 'Writesonic', 'Notion AI', 'GitHub Copilot',
    'Character.AI', 'Replika', 'YouChat', 'Poe', 'Forefront'
  ];

  // Prepare pie chart data
  const pieChartData = analytics?.engine_breakdown?.map((item, index) => ({
    name: item._id || 'Unknown',
    value: item.count,
    color: COLORS[index % COLORS.length]
  })) || [];

  // Prepare bar chart data
  const barChartData = analytics?.engine_breakdown?.slice(0, 8).map(item => ({
    name: item._id || 'Unknown',
    visits: item.count,
    queries: Math.floor(item.count * 0.7) // Simulate query conversion
  })) || [];
  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  return (
    // <div className="relative space-y-8 ">
    <div >
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-4 sm:space-y-0">
        <div>
          <h2 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">AI Traffic Analytics</h2>
          <p className="text-gray-600 mt-2">Track traffic from 15+ AI engines and analyze user behavior</p>
        </div>
        <div className="flex items-center space-x-4">
          <div className="relative">
            <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              className="pl-10 pr-4 py-3 border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all duration-200 bg-white/80 backdrop-blur-sm"
            >
              <option value="7_days">Last 7 Days</option>
              <option value="30_days">Last 30 Days</option>
              <option value="90_days">Last 90 Days</option>
            </select>
          </div>
        </div>
      </div>      {/* Summary Stats */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        <div className="group bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200/50 hover:shadow-xl hover:border-gray-300/50 transition-all duration-300 hover:-translate-y-1">
          <div className="p-6">
            <div className="flex items-center">
              <div className="p-3 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl mr-4 group-hover:scale-110 transition-transform duration-200">
                <TrendingUp className="h-6 w-6 text-white" />
              </div>
              <div className="flex-1">
                <div className="text-2xl font-bold text-gray-900">{analytics?.total_traffic || 0}</div>
                <div className="text-sm font-medium text-gray-600">Total Traffic</div>
              </div>
            </div>
          </div>
        </div>

        <div className="group bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200/50 hover:shadow-xl hover:border-gray-300/50 transition-all duration-300 hover:-translate-y-1">
          <div className="p-6">
            <div className="flex items-center">
              <div className="p-3 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl mr-4 group-hover:scale-110 transition-transform duration-200">
                <BarChart3 className="h-6 w-6 text-white" />
              </div>
              <div className="flex-1">
                <div className="text-2xl font-bold text-gray-900">{analytics?.engine_breakdown?.length || 0}</div>
                <div className="text-sm font-medium text-gray-600">AI Engines</div>
              </div>
            </div>
          </div>
        </div>

        <div className="group bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200/50 hover:shadow-xl hover:border-gray-300/50 transition-all duration-300 hover:-translate-y-1">
          <div className="p-6">
            <div className="flex items-center">
              <div className="p-3 bg-gradient-to-br from-purple-500 to-violet-600 rounded-xl mr-4 group-hover:scale-110 transition-transform duration-200">
                <Eye className="h-6 w-6 text-white" />
              </div>
              <div className="flex-1">
                <div className="text-lg font-bold text-gray-900">{analytics?.engine_breakdown?.[0]?._id || 'N/A'}</div>
                <div className="text-sm font-medium text-gray-600">Top Engine</div>
              </div>
            </div>
          </div>
        </div>

        <div className="group bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200/50 hover:shadow-xl hover:border-gray-300/50 transition-all duration-300 hover:-translate-y-1">
          <div className="p-6">
            <div className="flex items-center">
              <div className="p-3 bg-gradient-to-br from-yellow-500 to-orange-600 rounded-xl mr-4 group-hover:scale-110 transition-transform duration-200">
                <Calendar className="h-6 w-6 text-white" />
              </div>
              <div className="flex-1">
                <div className="text-lg font-bold text-gray-900">{analytics?.period?.replace('_', ' ') || 'N/A'}</div>
                <div className="text-sm font-medium text-gray-600">Period</div>
              </div>
            </div>
          </div>
        </div>
      </div>      {/* Traffic Trend Chart */}
      <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200/50">
        <div className="px-6 py-4 border-b border-gray-200/50">
          <h3 className="text-xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent flex items-center">
            <Activity className="h-5 w-5 mr-2 text-blue-500" />
            Traffic Trends
          </h3>
        </div>
        <div className="p-6">
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={timeSeriesData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                <XAxis 
                  dataKey="date" 
                  tick={{ fill: '#6b7280', fontSize: 12 }}
                  axisLine={{ stroke: '#e5e7eb' }}
                />
                <YAxis 
                  tick={{ fill: '#6b7280', fontSize: 12 }}
                  axisLine={{ stroke: '#e5e7eb' }}
                />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(229, 231, 235, 0.5)',
                    borderRadius: '12px',
                    boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)'
                  }}
                />
                <Line 
                  type="monotone" 
                  dataKey="traffic" 
                  stroke="url(#blueGradient)" 
                  strokeWidth={3}
                  name="Total Traffic"
                  dot={{ fill: '#3B82F6', strokeWidth: 2, r: 4 }}
                  activeDot={{ r: 6, stroke: '#3B82F6', strokeWidth: 2, fill: '#ffffff' }}
                />
                <Line 
                  type="monotone" 
                  dataKey="queries" 
                  stroke="url(#greenGradient)"
                  strokeWidth={3}
                  name="Queries"
                  dot={{ fill: '#10B981', strokeWidth: 2, r: 4 }}
                  activeDot={{ r: 6, stroke: '#10B981', strokeWidth: 2, fill: '#ffffff' }}
                />
                <defs>
                  <linearGradient id="blueGradient" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#3B82F6" />
                    <stop offset="100%" stopColor="#6366F1" />
                  </linearGradient>
                  <linearGradient id="greenGradient" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#10B981" />
                    <stop offset="100%" stopColor="#059669" />
                  </linearGradient>
                </defs>
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>      {/* Charts Grid */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* AI Engine Distribution */}
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200/50">
          <div className="px-6 py-4 border-b border-gray-200/50">
            <h3 className="text-xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent flex items-center">
              <BarChart3 className="h-5 w-5 mr-2 text-indigo-500" />
              AI Engine Distribution
            </h3>
          </div>
          <div className="p-6">
            <div className="h-80">
              {pieChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieChartData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                      stroke="#ffffff"
                      strokeWidth={2}
                    >
                      {pieChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={{
                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(229, 231, 235, 0.5)',
                        borderRadius: '12px',
                        boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)'
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-gray-500">
                  <div className="relative w-16 h-16 mb-4">
                    <div className="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl"></div>
                    <div className="absolute inset-2 bg-white rounded-xl flex items-center justify-center">
                      <BarChart3 className="h-6 w-6 text-gray-400" />
                    </div>
                  </div>
                  <p className="font-medium">No data available</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Engine Performance */}
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200/50">
          <div className="px-6 py-4 border-b border-gray-200/50">
            <h3 className="text-xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent flex items-center">
              <Zap className="h-5 w-5 mr-2 text-purple-500" />
              Engine Performance
            </h3>
          </div>
          <div className="p-6">
            <div className="h-80">
              {barChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={barChartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                    <XAxis 
                      dataKey="name" 
                      tick={{ fill: '#6b7280', fontSize: 12 }}
                      axisLine={{ stroke: '#e5e7eb' }}
                    />
                    <YAxis 
                      tick={{ fill: '#6b7280', fontSize: 12 }}
                      axisLine={{ stroke: '#e5e7eb' }}
                    />
                    <Tooltip 
                      contentStyle={{
                        backgroundColor: 'rgba(255, 255, 255, 0.95)',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(229, 231, 235, 0.5)',
                        borderRadius: '12px',
                        boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)'
                      }}
                    />
                    <Bar dataKey="visits" fill="url(#blueBarGradient)" name="Visits" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="queries" fill="url(#greenBarGradient)" name="Queries" radius={[4, 4, 0, 0]} />
                    <defs>
                      <linearGradient id="blueBarGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#3B82F6" />
                        <stop offset="100%" stopColor="#6366F1" />
                      </linearGradient>
                      <linearGradient id="greenBarGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#10B981" />
                        <stop offset="100%" stopColor="#059669" />
                      </linearGradient>
                    </defs>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-gray-500">
                  <div className="relative w-16 h-16 mb-4">
                    <div className="absolute inset-0 bg-gradient-to-br from-purple-100 to-violet-100 rounded-2xl"></div>
                    <div className="absolute inset-2 bg-white rounded-xl flex items-center justify-center">
                      <Zap className="h-6 w-6 text-purple-400" />
                    </div>
                  </div>
                  <p className="font-medium">No data available</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>      {/* Tracked AI Engines */}
      <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200/50">
        <div className="px-6 py-4 border-b border-gray-200/50">
          <h3 className="text-xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent flex items-center">
            <Eye className="h-5 w-5 mr-2 text-green-500" />
            Tracked AI Engines
          </h3>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
            {aiEngines.map((engine, index) => (
              <div key={engine} className="group flex items-center space-x-3 p-4 bg-gradient-to-r from-gray-50 to-gray-100/50 hover:from-blue-50 hover:to-indigo-50/50 rounded-xl border border-gray-200/50 hover:border-blue-200/50 transition-all duration-200 hover:shadow-md hover:-translate-y-0.5">
                <div className="w-3 h-3 rounded-full bg-gradient-to-r from-blue-400 to-indigo-500 group-hover:from-blue-500 group-hover:to-indigo-600 transition-all duration-200"></div>
                <span className="text-sm font-medium text-gray-900 group-hover:text-blue-900 transition-colors duration-200">{engine}</span>
              </div>
            ))}
          </div>
        </div>
      </div>      {/* UTM Campaign Tracking */}
      <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200/50">
        <div className="px-6 py-4 border-b border-gray-200/50">
          <h3 className="text-xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent flex items-center">
            <BarChart3 className="h-5 w-5 mr-2 text-orange-500" />
            Campaign Tracking
          </h3>
        </div>
        <div className="p-6">
          <div className="text-center py-8">
            <div className="relative mx-auto w-24 h-24 mb-6">
              <div className="absolute inset-0 bg-gradient-to-br from-orange-100 to-amber-100 rounded-2xl"></div>
              <div className="absolute inset-2 bg-white rounded-xl flex items-center justify-center shadow-lg">
                <BarChart3 className="h-8 w-8 text-orange-500" />
              </div>
            </div>
            <h4 className="text-xl font-bold text-gray-900 mb-2">UTM Campaign Analytics</h4>
            <p className="text-gray-600 mb-8 max-w-md mx-auto">
              Track campaign performance with UTM parameters. Data will appear here once campaigns are active.
            </p>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
              <div className="group bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100 hover:shadow-lg transition-all duration-200 hover:-translate-y-1">
                <div className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">0</div>
                <div className="text-sm font-medium text-blue-700 mt-1">Campaigns</div>
              </div>
              <div className="group bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 border border-green-100 hover:shadow-lg transition-all duration-200 hover:-translate-y-1">
                <div className="text-3xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">0</div>
                <div className="text-sm font-medium text-green-700 mt-1">Sources</div>
              </div>
              <div className="group bg-gradient-to-br from-purple-50 to-violet-50 rounded-xl p-6 border border-purple-100 hover:shadow-lg transition-all duration-200 hover:-translate-y-1">
                <div className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-violet-600 bg-clip-text text-transparent">0</div>
                <div className="text-sm font-medium text-purple-700 mt-1">Mediums</div>
              </div>
            </div>
          </div>        </div>
      </div>
      {!isMenuOpen && (
        <div className="absolute inset-0 bg-gray-900/40 backdrop-blur-sm z-50 pointer-events-none">
          <div className="fixed inset-0 flex items-baseline justify-center pointer-events-none top-32">
            <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl border border-gray-200/50 p-12 text-center pointer-events-auto">
              <div className="relative w-20 h-20 mx-auto mb-6">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-2xl"></div>
                <div className="absolute inset-2 bg-white rounded-xl flex items-center justify-center shadow-lg">
                  <Activity className="h-8 w-8 text-blue-500" />
                </div>
              </div>
              <h3 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent mb-4">
                Coming Soon
              </h3>
              <p className="text-gray-600 text-lg max-w-md mx-auto">
                AI Traffic Analytics feature is currently under development. Stay tuned for detailed insights!
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AITrafficAnalytics;
